import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {memo, type ReactElement} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    LabelSmall,
    styles as typographyStyles,
} from "@khanacademy/wonder-blocks-typography";

import type {ActivitySessionsV2Query_user_activityLogV2_time as ActivityTime} from "./__graphql-types__/ActivitySessionsV2Query.ts";

interface Props {
    activityTime: ActivityTime;
}

const ActivitySessionsTotalCounts = ({
    activityTime: {exerciseMinutes, totalMinutes},
}: Props): ReactElement => {
    return (
        <View style={styles.container}>
            <Body style={typographyStyles.Title}>{exerciseMinutes}</Body>
            <LabelSmall style={[styles.text, styles.twoLines]}>
                {t`exercise min`}
            </LabelSmall>
            <View style={styles.hairline} />
            <Body style={typographyStyles.Title}>{totalMinutes}</Body>
            <LabelSmall style={[styles.text, styles.twoLines]}>
                {t`total learning min`}
            </LabelSmall>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: "row",
        alignItems: "flex-end",
    },
    text: {
        color: semanticColor.core.foreground.neutral.strong,
        textOverflow: "ellipsis",
        marginBlockEnd: sizing.size_040,
    },
    hairline: {
        backgroundColor: semanticColor.core.foreground.neutral.strong,
        height: sizing.size_480,
        width: 1,
        marginInlineStart: sizing.size_080,
        marginInlineEnd: sizing.size_080,
    },
    twoLines: {
        // This is supported by the latest version of most browsers:
        // https://caniuse.com/#feat=css-line-clamp
        // Browsers that do not support it (such as older versions or IE) will
        // show max two lines as well but without the ellipsis.
        maxHeight: 36, // Twice the height of the text.
        overflow: "hidden",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
    },
});

export default memo(ActivitySessionsTotalCounts);
